import { useEffect, useMemo, useState } from "react";
import {
  DisableUIOverlayEvent,
  UIContextEvent,
  WarsEvents,
  SetGameConfigEvent,
  AudioCueEvent,
  PlayPreloadAudioEvent,
} from "@playroomkit/pr-wars/events";
import { useManager } from "@src/contexts/WarsContext";
import { useShop } from "@src/contexts/ShopProvider";
import {
  GameMode,
  UIContextType,
  gameTypeOptions,
  ShopItemType,
} from "@playroomkit/pr-wars-data";
import { WarsGame } from "@playroomkit/pr-wars";
import "./GameConfig.css";
import ScalableWrapper from "@components/ScalableWrapper";
import View from "@components/View";
import { images } from "@utils/media";
import Button from "@components/Button";
import { useDebounce } from "@hooks/useDebounce";
import { platform } from "@playroomkit/pr-common";
import * as playroom from "playroomkit";
import clsx from "clsx";

interface GameRoundOption {
  id: string;
  name: string;
  enabled: boolean;
  icon: string;
  isSelectable: boolean;
  required: boolean;
  trial: boolean;
  isPremium?: boolean;
  isPurchased?: boolean;
  price?: string | number;
}

interface Environment {
  id: string;
  name: string;
}

const trialOnServers = ["997752993598419044", "1237135601657184297"];

// FIXME: This shouldn't be hard coded here...
const allRounds = [
  {
    name: "Topic Thunder",
    id: "topic-thunder",
    enabled: true,
    disabled: false,
    thumbnail: "topic-thunder.webp",
    modes: [GameMode.ELIMINATION, GameMode.POINTS],
    required: true,
  },
  {
    name: "Pop Culture",
    id: "pop-culture",
    disabled: false,
    thumbnail: "pop-culture.webp",
    modes: [GameMode.ELIMINATION, GameMode.POINTS],
    trial: true,
    premium: true,
  },
  {
    name: "Pitch It",
    id: "pitch-it",
    disabled: false,
    thumbnail: "pitch-it.webp",
    modes: [GameMode.ELIMINATION, GameMode.POINTS],
    trial: true,
    premium: true,
  },
  {
    name: "Finish Me Off",
    id: "finish-me-off",
    disabled: false,
    thumbnail: "finish-me-off.webp",
    modes: [GameMode.ELIMINATION, GameMode.POINTS],
    trial: true,
    premium: true,
  },
  {
    name: "Return from Beyond",
    id: "return-from-beyond",
    enabled: true,
    disabled: false,
    thumbnail: "return-from-beyond.webp",
    modes: [GameMode.ELIMINATION],
  },
  {
    name: "Eldritch Sacrifice",
    id: "eldritch-sacrifice",
    disabled: false,
    thumbnail: "eldritch-sacrifice.webp",
    modes: [GameMode.ELIMINATION],
    premium: true,
  },
];

const getRounds = (
  manager: WarsGame,
  mode: GameMode,
  shopItems: any[],
  isRunningInPlayroomServer?: boolean
) => {
  const r: GameRoundOption[] = [];
  const player = manager.getMyPlayer();

  let gameRounds = [...allRounds];
  gameRounds = gameRounds.filter((round) => round.modes.includes(mode));

  for (let i = 0; i < gameRounds.length; ++i) {
    const round = gameRounds[i];
    const isPremium = round.premium;
    const isEnabledInPlayroomServer = Boolean(
      round.trial && isRunningInPlayroomServer
    );

    // Check if the round is purchased
    let isPurchased = false;
    let price: string | number | undefined;
    if (isPremium) {
      const shopItem = shopItems.find(
        (item) =>
          item.metadata?.type === ShopItemType.GAME_MODE &&
          item.metadata?.id === round.id
      );
      isPurchased = Boolean(shopItem?.purchased || player?.allAccess);
      price = shopItem?.price;
    }

    r.push({
      id: round.id,
      name: round.name,
      enabled: !!round?.enabled,
      icon: `img/Rounds/${round.id}.webp`,
      isSelectable:
        !round.required &&
        (!isPremium || isPurchased || isEnabledInPlayroomServer),
      required: Boolean(round.required),
      trial: Boolean(round.trial),
      isPremium: Boolean(isPremium),
      isPurchased,
      ...(price ? { price } : {}),
    });
  }

  return r;
};

const getAvailableGameEnvironments = (manager: WarsGame) => {
  let environments = manager.getAvailableEnvironments(),
    r: Environment[] = [];

  for (let i = 0; i < environments.length; ++i) {
    r.push({
      id: i.toString(),
      name: environments[i],
    });
  }

  return r;
};

const GameConfig: React.FC = () => {
  const manager = useManager();
  const { shopItems, toggleShopVisibility, onCategoryChange, handleClick } =
    useShop();

  const isRunningInPlayroomServer =
    platform.isRunningInDiscord
      ? (() => {
          const discordSDK = playroom.getDiscordClient();
          const serverId = discordSDK?.guildId ?? "";
          return trialOnServers.includes(serverId);
        })()
      : false;


  const [environments, setEnvironments] = useState<Environment[]>(
    getAvailableGameEnvironments(manager)
  );
  const [selectedEnvironment, setSelectedEnvironment] = useState<string | null>(
    null
  );
  const [showUI, setShowUI] = useState(false);
  const [gameMode, setGameMode] = useState<GameMode>(GameMode.ELIMINATION);
  const [gameRounds, setGameRounds] = useState<GameRoundOption[]>(
    getRounds(manager, gameMode, shopItems, isRunningInPlayroomServer)
  );
  const [showInstructions, setShowInstructions] = useState(
    localStorage.getItem("instructions") === "true"
  );

  useEffect(() => {
    const handleUIContextEvent = (event: Event) => {
      let e = event as UIContextEvent,
        display = e.hasContext(UIContextType.GAME_CONFIG);

      if (display) {
        setShowUI(!showUI);
      }
    };

    const handleStateSyncEvent = (event: Event) => {
      let gameRounds = getRounds(
        manager,
        gameMode,
        shopItems,
        isRunningInPlayroomServer
      );

      if (gameRounds.length > 0) {
        setGameRounds(gameRounds);
      }

      let environments = getAvailableGameEnvironments(manager);
      setEnvironments(environments);

      if (environments.length > 0) {
        setSelectedEnvironment(environments[0].name);
      }
    };

    document.addEventListener(WarsEvents.UIContextEvent, handleUIContextEvent);
    document.addEventListener(WarsEvents.StateSyncEvent, handleStateSyncEvent);
    document.addEventListener(WarsEvents.StartGameEvent, handleStartGame);

    // Cleanup function to remove event listeners
    return () => {
      document.removeEventListener(
        WarsEvents.UIContextEvent,
        handleUIContextEvent
      );
      document.removeEventListener(
        WarsEvents.StateSyncEvent,
        handleStateSyncEvent
      );
      document.removeEventListener(WarsEvents.StartGameEvent, handleStartGame);
    };
  }, [
    showUI,
    gameRounds,
    environments,
    gameMode,
    shopItems,
    isRunningInPlayroomServer,
  ]);

  // Update game rounds when shop items change (e.g., after a purchase)
  useEffect(() => {
    const newGameRounds = getRounds(
      manager,
      gameMode,
      shopItems,
      isRunningInPlayroomServer
    );

    if (newGameRounds.length > 0) {
      setGameRounds(newGameRounds);
    }
  }, [shopItems, gameMode, isRunningInPlayroomServer]);

  const handleChangeGameMode = useDebounce((mode: GameMode) => {
    manager.changeGameMode(mode);
  }, 150);

  const toggleGameRound = (id: string) => {
    setGameRounds((prevModes) => {
      const modeToToggle = prevModes.find((mode) => mode.id === id);
      if (!modeToToggle) return prevModes;

      // Prevent toggling if required
      if (modeToToggle.required) return prevModes;

      // If trying to disable it, ensure at least one other non-required mode stays enabled
      if (modeToToggle.enabled) {
        const enabledNonRequired = prevModes.filter(
          (mode) => !mode.required && mode.enabled
        );
        if (enabledNonRequired.length <= 1) {
          // This is the last enabled non-required option — do not allow unchecking
          return prevModes;
        }
        document.dispatchEvent(new PlayPreloadAudioEvent('sfx', 'point', 1, false));
      }

      // Toggle the mode
      return prevModes.map((mode) =>
        mode.id === id ? { ...mode, enabled: !mode.enabled } : mode
      );
    });
  };

  const handleClose = () => {
    setShowUI(false);
    document.dispatchEvent(new DisableUIOverlayEvent());
  };

  const handleGameModeChange = (mode: GameMode) => {
    document.dispatchEvent(new PlayPreloadAudioEvent('sfx', 'point', 1, false));
    setGameMode(mode);
    setGameRounds(getRounds(manager, mode, shopItems, isRunningInPlayroomServer));
    handleChangeGameMode(mode);
  };

  const handleShowInstructionsChange = (status: boolean) => {
    document.dispatchEvent(new PlayPreloadAudioEvent('sfx', 'point', 1, false));
    setShowInstructions(status);
    localStorage.setItem("instructions", status ? "true" : "false");
  };

  const handleStartGame = () => {
    const players = manager.getPlayers();

    if (players.length < 4) {
      const event = new UIContextEvent(UIContextType.ADD_PLAYERS);
      document.dispatchEvent(event);
      return;
    }

    setShowUI(true);
  };

  const startGame = async () => {
    setShowUI(false);
    document.dispatchEvent(new DisableUIOverlayEvent());

    let gameRoundIds: string[] = [],
      botIds: string[] = [];

    for (let i = 0; i < gameRounds.length; ++i) {
      if (gameRounds[i].enabled) {
        gameRoundIds.push(gameRounds[i].id);
      }
    }

    // FIXME: This should be in the options UI
    let totalRounds = -1;
    if (gameMode === GameMode.POINTS) {
      totalRounds = 5;
    }

    const event = new SetGameConfigEvent(
      gameMode,
      totalRounds,
      gameRoundIds,
      selectedEnvironment!,
      botIds,
      showInstructions
    );
    document.dispatchEvent(event);

    await manager.startGame(
      gameMode,
      totalRounds,
      gameRoundIds,
      selectedEnvironment!,
      botIds,
      showInstructions
    );
  };

  const redirectToPlayroomServer = async () => {
    const inviteURL = "https://discord.gg/62qkEA3raD";

    if (platform.isRunningInDiscord) {
      const discordSdk = playroom.getDiscordClient();
      await discordSdk?.commands.openExternalLink({
        url: inviteURL,
      });
    } else {
      window.open(inviteURL, "_blank");
    }
  };

  const handlePurchaseGameMode = (roundId: string) => {
    const productId = shopItems.find(
      (item) =>
        item.metadata?.type === ShopItemType.GAME_MODE &&
        item.metadata?.id === roundId
    )?.sku_id;

    if (productId) {
      handleClick(productId);
    } else {
      onCategoryChange(ShopItemType.GAME_MODE);
      toggleShopVisibility(true);
      handleClose();
    }
  };

  return (
    <>
      {showUI && gameRounds.length > 0 && (
        <View
          enableEventCapturing
          className="w-screen h-dvh absolute top-0 left-0 z-[999999] font-[Slackey]"
        >
          <ScalableWrapper maintainAspectRatioBy="both">
            <View
              className="bg-cover bg-center bg-no-repeat w-full h-full flex items-center justify-center"
              style={{ backgroundImage: `url(${images.GameConfigBackground})` }}
            ></View>
          </ScalableWrapper>
          <ScalableWrapper maintainAspectRatioBy="height" anchor="top-left">
            <Button
              className="absolute top-4 left-0 bg-transparent border-none outline-none"
              onClick={handleClose}
            >
              <img
                src={images.BlackLeftArrowIcon}
                alt="Back"
                className="w-14 h-14 object-contain"
              />
            </Button>
          </ScalableWrapper>
          <ScalableWrapper maintainAspectRatioBy="height">
            <View className="w-full h-full flex items-center justify-center">
              <View
                enableEventCapturing
                className="max-w-[1024px] w-full h-full flex flex-col gap-4 overflow-y-auto no-scrollbar"
              >
                <section className="relative w-full h-24 flex flex-col items-center justify-center gap-2 pt-4">
                  <h1
                    className="text-4xl text-white my-0"
                    style={{ textShadow: "3px 3px 0px rgba(0, 0, 0, 1)" }}
                  >
                    Ready?
                  </h1>
                  <p className="my-0">Let's make some final tweaks!</p>
                </section>
                <section className="w-full flex px-8 gap-20">
                  <div className="flex flex-col">
                    <h3>Game Modes</h3>
                    <div className="flex items-center justify-start gap-4">
                      {gameTypeOptions.map((option) => (
                        <Button
                          key={option.id}
                          onClick={() => handleGameModeChange(option.id)}
                          className="relative w-40 h-36 bg-white border border-black rounded-md p-2 font-[Slackey]"
                        >
                          <h1 className="leading-none">{option.name}</h1>
                          <p className="absolute bottom-2 translate-x-1/2 translate-y-1/2 text-xs">
                            {option.type}
                          </p>
                          <img
                            src={images.CheckboxIcon}
                            alt="Checkbox"
                            className="absolute top-1 right-1 w-6 h-6 object-contain"
                          />
                          {option.id === gameMode && (
                            <img
                              src={images.BlackTickIcon}
                              alt="Tick"
                              className="absolute -top-2 -right-2 w-8 h-8 object-contain"
                            />
                          )}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div className="w-full flex flex-col gap-4">
                    <div>
                      <h3 className="leading-none">Instructions at Start</h3>
                      <Button
                        onClick={() =>
                          handleShowInstructionsChange(!showInstructions)
                        }
                        className="w-44 h-10 bg-white border border-black rounded-md font-[Slackey] text-lg"
                      >
                        {showInstructions ? "ON" : "OFF"}
                      </Button>
                    </div>
                  </div>
                </section>

                <section className="w-full flex-col px-8">
                  <h3>Rounds available in this mode</h3>
                  <div className="grid grid-cols-3 md:grid-cols-4 items-center justify-start gap-4 pb-32">
                    {gameRounds.map((round) => {
                      if (
                        round.trial &&
                        !isRunningInPlayroomServer &&
                        !round.isPurchased
                      ) {
                        return (
                          <div key={round.id} className="space-y-2">
                            <Button
                              key={round.id}
                              className="relative w-full h-36 bg-black border-none outline outline-2 outline-black font-[Slackey] rounded-xl flex-col overflow-hidden p-0"
                              onClick={redirectToPlayroomServer}
                            >
                              <p className="text-[0.6rem] text-white my-0.5">
                                Try it on Playroom Server!
                              </p>
                              <View
                                className="w-full h-full bg-transparent bg-cover bg-no-repeat"
                                style={{
                                  backgroundImage: `url(${round.icon})`,
                                  backgroundPositionY: "-10px",
                                }}
                              />
                            </Button>
                            <GameRoundButton
                              round={round}
                              onClick={() => handlePurchaseGameMode(round.id)}
                            />
                          </div>
                        );
                      }

                      // Show locked state for premium rounds that aren't purchased
                      if (
                        round.isPremium &&
                        !round.isPurchased &&
                        !isRunningInPlayroomServer &&
                        !round.trial
                      ) {
                        return (
                          <div key={round.id} className="space-y-2">
                            <div
                              key={round.id}
                              className="relative h-36 bg-transparent bg-cover bg-no-repeat border-none outline-none p-2 font-[Slackey] rounded-xl overflow-hidden"
                              style={{ backgroundImage: `url(${round.icon})` }}
                            >
                              {/* Locked overlay */}
                              <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center" />
                            </div>
                            <GameRoundButton
                              round={round}
                              onClick={() => handlePurchaseGameMode(round.id)}
                            />
                          </div>
                        );
                      }

                      return (
                        <div key={round.id} className="space-y-2">
                          <div
                            className="relative h-36 bg-transparent bg-cover bg-no-repeat border-none outline-none p-2 font-[Slackey] rounded-xl"
                            style={{ backgroundImage: `url(${round.icon})` }}
                          />
                          <GameRoundButton
                            round={round}
                            onClick={() => toggleGameRound(round.id)}
                          />
                        </div>
                      );
                    })}
                  </div>
                </section>
              </View>
            </View>
          </ScalableWrapper>
          <ScalableWrapper maintainAspectRatioBy="height" anchor="bottom">
            <View className="w-full h-full flex items-end justify-center pb-10">
              <Button
                onClick={startGame}
                className="relative w-72 h-16 bg-transparent border-none outline-none font-[Slackey] text-xl"
              >
                <img
                  src={images.ButtonIcon}
                  alt="Button"
                  className="w-full h-full object-contain"
                />
                <View className="absolute top-0 left-0 w-full h-full">
                  <p>Let's Go</p>
                </View>
              </Button>
            </View>
          </ScalableWrapper>
        </View>
      )}
    </>
  );
};

const GameRoundButton = ({
  round,
  onClick,
}: {
  round: GameRoundOption;
  onClick: () => void;
}) => {
  const { showPrice } = useShop();
  const isOn = round.enabled;
  const isNotPurchased =
    !round.isSelectable && round.isPremium && !round.isPurchased;

  return (
    <Button
      onClick={onClick}
      className={clsx(
        "w-full rounded-lg p-1 inline-block relative shadow-[2px_3px_0_0_#000] border-none outline-none",
        {
          "bg-[#DBFF10]": isOn,
          "bg-black": !isOn && round.isSelectable,
          "bg-[#FF8838]": isNotPurchased,
        }
      )}
    >
      <div
        className={clsx(
          "font-[Slackey]  text-base rounded-md px-4 py-2 flex items-center justify-center gap-2 transition-all whitespace-nowrap relative z-10 text-black",
          {
            "bg-[#63EA10]": isOn,
            "bg-[#828282]": !isOn && round.isSelectable,
            "bg-[#FF65EB]": isNotPurchased,
          }
        )}
      >
        {isNotPurchased && (
          <>
            <img
              src={images.LockIcon}
              alt="Lock"
              className="w-6 h-6 object-contain"
            />
            <span>Buy {round.price && showPrice(round.price)}</span>
          </>
        )}
        {isOn ? <span>ON</span> : round.isSelectable ? <span>OFF</span> : null}
      </div>
    </Button>
  );
};

export default GameConfig;
